import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined"
import React, { useEffect, useState } from "react"
import { SEO } from "../../components/SEO"
import { PATHS } from "../../constants/paths"
import Button from "@mui/material/Button"
import { Link, useParams } from "react-router-dom"
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import Box from "@mui/material/Box"
import { Container } from "@mui/material"
import Typography from "@mui/material/Typography"
import { StatCard } from "./components/StatCard"
import Overview from "./components/tabs/Overview"
import Leaderboard from "./components/tabs/Leaderboard"
import Rules from "./components/tabs/Rules"
import { getTournamentDetails } from "../../api/tournaments"
import NotFoundPage from "../NotFoundPage"
import { formatCountdown } from "./utils/formatCountdown"
import { useCountdown } from "./utils/useCountdown"
import { useAppDispatch, useAppSelector } from "../../store"
import { connectHandler } from "../../utils/connectHandler"
import { useTournamentDialogs } from "./hooks/useTournamentDialogs"
import { TournamentActionButton } from "./components/TournamentActionButton"

const tabs = [
  {
    id: "overview",
    label: "OVERVIEW",
  },
  {
    id: "leaderboard",
    label: "LEADERBOARD",
  },
  {
    id: "rules",
    label: "RULES",
  },
]

export const TournamentDetailsPage = () => {
  const { id } = useParams()

  const dispatch = useAppDispatch()
  const { user } = useAppSelector((state) => state.user)

  const [activeTab, setActiveTab] = useState("overview")
  const [tournament, setTournament] = useState(null)
  const [prizes, setPrizes] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const remainingSeconds = useCountdown(tournament)
  const [leaderboardCache, setLeaderboardCache] = useState({
    data: [],
    updatedAt: 0,
    userAddress: null,
    search: "",
  })

  const { openJoin, openGame } = useTournamentDialogs({ tournament, user })

  useEffect(() => {
    setLeaderboardCache({
      data: [],
      updatedAt: 0,
      userAddress: null,
    })
  }, [id, user?.address])

  useEffect(() => {
    loadTournament()
  }, [user])

  const loadTournament = async () => {
    setIsLoading(true)

    const { success, tournament, prizes } = await getTournamentDetails(
      id,
      user?.address,
    )

    if (success) {
      setTournament(tournament)
      setPrizes(prizes)
    }

    setIsLoading(false)
  }

  if (isLoading) {
    return null
  }

  if (!tournament) {
    return <NotFoundPage />
  }

  return (
    <Box>
      <SEO
        title={`Zombie TD | ${tournament.title} Tournament`}
        description={`Join the ${tournament.title} tournament, compete with players and win USDT rewards.`}
        ogTitle={`Zombie TD | ${tournament.title} Tournament`}
        ogDescription="Top players. Bigger prize pool. Only the strongest survivors will claim the rewards."
        ogUrl={`${PATHS.TOURNAMENT_DETAILS.PATH}`}
      />
      <Box className="content">
        <Box className="content-head" mb={4} mt={2}>
          <Container fixed>
            {/* Back button */}
            <Button
              component={Link}
              to={
                tournament.is_finished
                  ? PATHS.TOURNAMENTS_HISTORY.PATH
                  : PATHS.TOURNAMENTS.PATH
              }
              startIcon={<ArrowBackIosNewOutlinedIcon />}
              sx={{
                px: 0,
                color: "#8f877b",
                fontWeight: 900,
                "& .MuiButton-startIcon": {
                  mr: 0.5,
                },
                "& svg": {
                  fontSize: "12px !important",
                },
                "&:hover": {
                  color: "#d69a49",
                  background: "transparent",
                },
              }}
            >
              {tournament.is_finished
                ? "BACK TO TOURNAMENTS HISTORY"
                : "BACK TO TOURNAMENTS"}
            </Button>

            <Box
              sx={{
                background: `url(${tournament.image_hero}) center / cover no-repeat, #000`,
                py: 2,
              }}
            >
              <Typography variant="h1">{tournament.title}</Typography>

              <Typography
                variant="subtitle1"
                textAlign="left"
                sx={{
                  mb: 2,
                  color: "#dedede",
                  whiteSpace: "pre-line",
                }}
              >
                {tournament.brief}
              </Typography>
            </Box>

            {/* Summary statistics */}
            <Box
              sx={{
                display: "flex",
                flexDirection: {
                  xs: "column",
                  lg: "row",
                },
                alignItems: {
                  xs: "stretch",
                  lg: "center",
                },
                justifyContent: "space-between",
                mt: 3,
                gap: 1.5,
                mb: 6,
              }}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                  flexWrap: "wrap",
                  border: "1px solid #121212",
                  borderRadius: 1,
                  gap: 1.5,
                  background: "linear-gradient(180deg, #000 60%, #121212 100%)",
                }}
              >
                {[
                  {
                    icon: <EmojiEventsOutlinedIcon />,
                    label: "PRIZE POOL",
                    value: tournament.prize_pool + " USDT",
                  },
                  {
                    icon: <EmojiEventsOutlinedIcon />,
                    label: "ENTRY FEE",
                    value:
                      tournament.entry_fee > 0
                        ? tournament.entry_fee + " USDT"
                        : "Free",
                  },
                  {
                    icon: <GroupsOutlinedIcon />,
                    label: "PLAYERS",
                    value: tournament.players,
                    focus: false,
                  },
                  {
                    icon: <AccessTimeOutlinedIcon />,
                    label: tournament.is_finished
                      ? "FINISHED"
                      : tournament.starts_in
                        ? "STARTS IN"
                        : "TIME LEFT",
                    value: tournament.is_finished
                      ? tournament.end_at
                      : formatCountdown(remainingSeconds, true),
                    focus: false,
                    minWidth: {
                      xs: "100%",
                      sm: 238,
                    },
                  },
                ].map((stat) => (
                  <StatCard {...stat} />
                ))}
              </Box>

              {tournament.is_finished ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.6,
                    color: "#d69a49",
                    textTransform: "uppercase",
                    fontWeight: 900,
                    "& svg": {
                      fontSize: 26,
                    },
                  }}
                >
                  <CheckCircleOutlineIcon /> Finished
                </Box>
              ) : (
                <TournamentActionButton
                  tournament={tournament}
                  user={user}
                  fullWidth={false}
                  onConnect={() => connectHandler(dispatch)}
                  onJoin={openJoin}
                  onPlay={openGame}
                />
              )}
            </Box>

            {/* Tabs */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 2.4,
                overflowX: "auto",
                borderBottom: "1px solid #121212",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  minWidth: "max-content",
                }}
              >
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id

                  return (
                    <Button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      sx={{
                        px: 2.4,
                        py: 1.15,
                        color: isActive ? "#c49654" : "#9c9284",
                        fontWeight: 900,
                        borderRadius: "4px 4px 0 0",
                        border: isActive
                          ? "1px solid rgba(196,150,84,0.1)"
                          : "1px solid transparent",
                        background: isActive
                          ? "rgba(196,150,84,0.06)"
                          : "transparent",
                        "&:hover": {
                          borderColor: "rgba(198,146,76,0.45)",
                          background: "rgba(198,146,76,0.08)",
                        },
                      }}
                    >
                      {tab.label}
                    </Button>
                  )
                })}
              </Box>
            </Box>

            {activeTab === "overview" && <Overview tournament={tournament} />}
            {activeTab === "leaderboard" && (
              <Leaderboard
                tournament={tournament}
                cache={leaderboardCache}
                onCacheChange={setLeaderboardCache}
              />
            )}
            {activeTab === "rules" && <Rules />}
          </Container>
        </Box>
      </Box>
    </Box>
  )
}
